"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import "../AutoVerkaufen.css";

import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";
import { TopSectionAutoVerkaufen } from "../sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";
import höchstenPreis from "@/assets/auto-verkaufen/wir-kaufen-dein-auto-verkauf.avif";
import vonZuhauseAus from "@/assets/auto-verkaufen/Verkauft-handy.avif";
import { fragenKaernten } from "../data";
import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";

// Only needed after step 11; keep auth-related code/CSS out of initial paint.
// Below-the-fold section; defer to reduce initial route JS/CSS.
const Ratgeber = dynamic(() => import("@/components/Ratgeber/Ratgeber"), {
  ssr: false,
});

const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function Kaernten() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionAutoVerkaufen
        title={<>Auto in Kärnten verkaufen: Sicher & stressfrei</>}
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung für Kärnten.
          </>
        }
        buttonTitle="Bewertung ansehen"
        linkname="Kärnten"
        link="/auto-verkaufen/kaernten"
        customNav="Auto verkaufen"
        customNavLink="/auto-verkaufen"
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen/kaernten"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color AV-below-fold">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Dein Auto in Kärnten verkaufen - warum es oft komplizierter ist, als man denkt"
              leftText={
                <>
                  <p>
                    Wenn du dein Auto in Kärnten verkaufen möchtest, merkst du
                    schnell, dass der Prozess zeitaufwendig sein kann. Die
                    Nachfrage nach Gebrauchtwagen ist zwar hoch, besonders in
                    Klagenfurt, Villach und den Tourismusregionen. Gefragt sind
                    Klein- und Mittelklassewagen für Pendler, aber auch SUVs,
                    Vans und Allradfahrzeuge für Bergregionen und Familien. Viele
                    Autos in Kärnten haben durch Fahrten über Alpenpässe oder
                    durch die Wintersaison stärkere Belastungen.
                  </p>
                  <p>
                    Inserate, Preisverhandlungen und unsichere Käufer machen den
                    Verkauf zusätzlich schwierig. Mit Autoeinfachlos verkaufst du
                    dein Auto in Kärnten schnell, sicher und transparent.
                  </p>
                </>
              }
              rightFirstTitle="Auto verkaufen in Kärnten - worauf kommt es an?"
              rightFirstText={
                <>
                  <p>
                    Der Kärntner Automarkt ist vielseitig. In Städten wie
                    Klagenfurt oder Villach sind vor allem sparsame Stadtwagen
                    gefragt. In den ländlichen Regionen und Tourismusorten
                    wiederum haben SUVs, Kombis und Allradmodelle eine besonders
                    hohe Nachfrage.
                  </p>

                  <p>
                    <strong>Typische Probleme beim Auto verkaufen in Kärnten:</strong>
                  </p>
                  <ul className="custom-unordered-list">
                    <li>zeitintensive Inserate und viele Anfragen</li>
                    <li>Unsicherheit beim richtigen Preis</li>
                    <li>unseriöse Käufer oder Zahlungsverzögerungen</li>
                  </ul>

                  <p>
                    Mit Autoeinfachlos musst du dich darum nicht kümmern. Wir
                    übernehmen den gesamten Verkaufsprozess für dich.
                  </p>

                  <h3>Dein Vorteil mit Autoeinfachlos in Kärnten</h3>
                  <p>Mit Autoeinfachlos verkaufst du dein Auto stressfrei und effizient:</p>
                  <ul className="custom-unordered-list">
                    <li>
                      Zugang zu über 5.000 Händlern, viele davon auch in Kärnten
                    </li>
                    <li>
                      Auktion mit mehreren Bietern, so erzielst du automatisch den
                      besten Preis
                    </li>
                    <li>
                      Abholung direkt bei dir, egal ob in Klagenfurt, Villach
                      oder im ländlichen Raum
                    </li>
                    <li>schnelle und sichere Zahlung</li>
                  </ul>
                  <p>So machst du den Autoverkauf für dich unkompliziert und sicher.</p>
                </>
              }
              rightSecondTitle="Autoankauf in Kärnten - privat oder Händler?"
              rightSecondText={
                <>
                  <p>
                    Viele beginnen mit dem Privatverkauf. Doch beim Auto
                    verkaufen privat in Kärnten treten oft Nachteile auf:
                  </p>
                  <ul className="custom-unordered-list secondary">
                    <li>Inserate kosten Zeit und Geld</li>
                    <li>unzuverlässige Käufer oder Terminabsagen</li>
                    <li>Preisdrückerei und lange Wartezeiten</li>
                  </ul>

                  <p>Der Autoankauf über unser Händlernetzwerk ist die bessere Lösung:</p>
                  <ul className="custom-unordered-list secondary">
                    <li>geprüfte Käufer</li>
                    <li>sichere Abwicklung</li>
                    <li>schnelle Zahlung</li>
                  </ul>

                  <p>
                    Mit Autoeinfachlos kombinierst du die Reichweite vieler
                    Interessenten mit der Sicherheit professioneller Händler.
                  </p>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={höchstenPreis}
                h2
                isBig
                fontSize="36px"
                title="Was ist dein Auto in Kärnten wert?"
                alt="Auto in Kärnten bewerten"
                text={
                  <>
                    <p>Der Preis deines Autos hängt von mehreren Faktoren ab:</p>
                    <ul className="custom-unordered-list">
                      <li>Marke und Modell</li>
                      <li>Baujahr und Kilometerstand</li>
                      <li>allgemeiner Zustand und eventuelle Schäden</li>
                      <li>regionale Nachfrage in Kärnten</li>
                    </ul>

                    <p>
                      Mit Autoeinfachlos kannst du dein Auto kostenlos bewerten
                      lassen. In wenigen Sekunden erhältst du eine erste
                      Einschätzung und kannst direkt in die Auktion starten.
                    </p>
                    <p>
                      👉{" "}
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto bewerten
                      </Link>
                    </p>

                    <h3>So funktioniert der Verkauf bei Autoeinfachlos in Kärnten</h3>
                    <p>Der Ablauf ist einfach, transparent und auf Dich zugeschnitten:</p>
                    <ul className="custom-unordered-list">
                      <li>Auto online bewerten lassen – kostenlos und unverbindlich.</li>
                      <li>
                        Auktion starten – Dein Auto wird in unserem Händlernetzwerk
                        präsentiert.
                      </li>
                      <li>
                        Höchstgebot erhalten – mehrere Händler bieten, du
                        entscheidest.
                      </li>
                      <li>
                        Abholung vor Ort in Kärnten – Dein Auto wird direkt bei dir
                        in Kärnten übernommen.
                      </li>
                      <li>Sichere Bezahlung – schnell und unkompliziert.</li>
                    </ul>
                    <p>
                      Damit sparst du Zeit, verhinderst unnötigen Stress und
                      erhältst den besten Preis für dein Fahrzeug.
                    </p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufige Fragen rund um den Autoverkauf in Kärnten"
              fragen={fragenKaernten}
              image={FAQImage}
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Auto verkaufen in Kärnten"
                autoVerkaufen
                autoBewerten
                listenpreisBerechnen
                fahrgestellnummerPruefen
                kaputtesAuto
                wasistmeinautowert
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={vonZuhauseAus}
                h2
                isBig
                fontSize="36px"
                title="Auto verkaufen in Kärnten war noch nie so einfach"
                alt="Auto verkaufen in Kärnten war noch nie so einfach"
                text={
                  <>
                    <p>
                      Wenn du dein Auto in Kärnten verkaufen möchtest, musst du
                      dich nicht mit zeitaufwendigen Inseraten oder unsicheren
                      Käufern beschäftigen. Mit Autoeinfachlos profitierst du von
                      einem großen Händlernetzwerk, einer regionalen Abwicklung
                      und einer sicheren Bezahlung.
                    </p>
                    <p>
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto kostenlos bewerten & verkaufen
                      </Link>
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-bewerten"
            title="Jetzt Auto kostenlos bewerten und verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default Kaernten;
